import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Button,
  EmptyState,
  Icon,
  Skeleton,
  Switch,
  cn,
} from "@nexus/ui";
import {
  adminNotificationSettingsQueryOptions,
  useUpdateMarketplaceNotificationSetting,
} from "@/lib/queries";
import type { MarketplaceNotificationSettingsRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import {
  ALL_SLACK_TOGGLE_KEYS,
  countEnabledSlackToggles,
  SLACK_TOGGLE_GROUPS,
  toSlackTogglePatch,
  type SlackToggleDef,
  type SlackToggleGroup,
  type SlackToggleSettings,
} from "../lib/admin-slack-notifications";

interface AdminSlackNotificationsProps {
  canView: boolean;
  canEdit: boolean;
}

export function AdminSlackNotifications({ canView, canEdit }: AdminSlackNotificationsProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    ...adminNotificationSettingsQueryOptions(),
    enabled: canView,
  });
  const updateSetting = useUpdateMarketplaceNotificationSetting();

  if (!canView) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {t("common.noPermission", "You don't have permission to view this content.")}
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex max-w-3xl flex-col gap-8">
        <Skeleton className="h-16 w-full rounded-md" />
        <Skeleton className="h-48 w-full rounded-md" />
        <Skeleton className="h-36 w-full rounded-md" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <EmptyState
        variant="error"
        title={t(
          "marketplace.admin.slackNotifications.errorTitle",
          "Could not load Slack notification settings",
        )}
        description={t("errors.tryAgain", "Please try again.")}
      />
    );
  }

  const settings = data;
  const enabledCount = countEnabledSlackToggles(settings);

  function patchSettings(patch: Partial<SlackToggleSettings>) {
    updateSetting.mutate(patch);
  }

  return (
    <div className="flex max-w-3xl flex-col gap-8">
      <SlackDestinationHeader
        slackConfigured={settings.slackConfigured}
        enabledCount={enabledCount}
        totalCount={ALL_SLACK_TOGGLE_KEYS.length}
        updatedByName={settings.updatedByName}
      />

      {!settings.slackConfigured ? <SlackWebhookWarning /> : null}

      <div className="flex flex-col gap-8">
        {SLACK_TOGGLE_GROUPS.map((group) => (
          <SlackToggleGroupSection
            key={group.id}
            group={group}
            settings={settings}
            canEdit={canEdit}
            onPatch={patchSettings}
          />
        ))}
      </div>
    </div>
  );
}

function SlackDestinationHeader({
  slackConfigured,
  enabledCount,
  totalCount,
  updatedByName,
}: {
  slackConfigured: boolean;
  enabledCount: number;
  totalCount: number;
  updatedByName: string | null;
}) {
  const { t } = useTranslation();
  const countCopy = slackConfigured
    ? t("marketplace.admin.slackNotifications.postingCount", {
        defaultValue: "{{on}} of {{total}} posting",
        on: enabledCount,
        total: totalCount,
      })
    : t("marketplace.admin.slackNotifications.armedCount", {
        defaultValue: "{{on}} of {{total}} on",
        on: enabledCount,
        total: totalCount,
      });

  return (
    <header className="flex flex-col gap-3 border-b border-border pb-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <p className="font-mono text-[10px] font-medium uppercase tracking-label text-muted-foreground">
          {t("marketplace.admin.slackNotifications.channelKicker", "Channel")}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <p className="font-mono text-sm tracking-tight text-foreground">
            {t("marketplace.admin.slackNotifications.channel", "#marketplace-orders")}
          </p>
          {slackConfigured ? (
            <Badge variant="status-connected">
              {t("marketplace.admin.slackNotifications.posting", "Posting")}
            </Badge>
          ) : (
            <Badge variant="status-in-production" dot>
              {t("marketplace.admin.slackNotifications.webhookMissing", "Webhook missing")}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-0.5 sm:items-end">
        <p className="text-xs tabular-nums text-muted-foreground">{countCopy}</p>
        {updatedByName ? (
          <p className="text-xs text-muted-foreground">
            {t("marketplace.admin.slackNotifications.lastUpdated", {
              defaultValue: "Last updated by {{name}}",
              name: updatedByName,
            })}
          </p>
        ) : null}
        <p className="text-[11px] text-muted-foreground" role="status">
          {t("marketplace.admin.slackNotifications.savesImmediately", "Saves as you toggle")}
        </p>
      </div>
    </header>
  );
}

function SlackWebhookWarning() {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-md border border-warning-border bg-warning-subtle px-4 py-3"
    >
      <Icon
        name="alert-circle"
        size="sm"
        color="inherit"
        className="mt-0.5 text-warning-subtle-foreground"
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-warning-subtle-foreground">
          {t("marketplace.admin.slackNotifications.noWebhookTitle", "Nothing will post")}
        </p>
        <p className="mt-0.5 max-w-[68ch] text-xs leading-relaxed text-warning-subtle-foreground">
          {t(
            "marketplace.admin.slackNotifications.noWebhook",
            "Enabled alerts have nowhere to go until the marketplace Slack webhook is configured.",
          )}
        </p>
      </div>
    </div>
  );
}

function SlackToggleGroupSection({
  group,
  settings,
  canEdit,
  onPatch,
}: {
  group: SlackToggleGroup;
  settings: MarketplaceNotificationSettingsRow;
  canEdit: boolean;
  onPatch: (patch: Partial<SlackToggleSettings>) => void;
}) {
  const { t } = useTranslation();
  const keys = group.toggles.map((toggle) => toggle.key);
  const enabledCount = countEnabledSlackToggles(settings, keys);
  const title = t(group.titleKey, group.titleDefault);
  const allOn = enabledCount === keys.length;
  const allOff = enabledCount === 0;
  const showGroupActions = canEdit && keys.length > 1;

  return (
    <section aria-labelledby={`mp-slack-group-${group.id}`}>
      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2
            id={`mp-slack-group-${group.id}`}
            className="m-0 text-sm font-semibold tracking-tight text-foreground"
          >
            {title}
          </h2>
          <p className="m-0 mt-0.5 max-w-[62ch] text-xs leading-relaxed text-muted-foreground">
            {t(group.descriptionKey, group.descriptionDefault)}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className="px-1 font-mono text-[10px] tabular-nums text-muted-foreground">
            {t("marketplace.admin.slackNotifications.groupCount", {
              defaultValue: "{{on}}/{{total}}",
              on: enabledCount,
              total: keys.length,
            })}
          </span>
          {showGroupActions && !allOn ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="cursor-pointer text-muted-foreground"
              onClick={() => onPatch(toSlackTogglePatch(keys, true))}
              aria-label={t("marketplace.admin.slackNotifications.turnGroupOnAria", {
                defaultValue: "Turn on all {{group}} alerts",
                group: title,
              })}
            >
              {t("marketplace.admin.slackNotifications.turnGroupOn", "All on")}
            </Button>
          ) : null}
          {showGroupActions && !allOff ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="cursor-pointer text-muted-foreground"
              onClick={() => onPatch(toSlackTogglePatch(keys, false))}
              aria-label={t("marketplace.admin.slackNotifications.turnGroupOffAria", {
                defaultValue: "Turn off all {{group}} alerts",
                group: title,
              })}
            >
              {t("marketplace.admin.slackNotifications.turnGroupOff", "All off")}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="divide-y divide-border/70 overflow-hidden rounded-md border border-border/70 bg-card">
        {group.toggles.map((toggle) => (
          <SlackToggleRow
            key={toggle.key}
            toggle={toggle}
            checked={settings[toggle.key]}
            disabled={!canEdit}
            onCheckedChange={(checked) => onPatch({ [toggle.key]: checked })}
          />
        ))}
      </div>
    </section>
  );
}

function SlackToggleRow({
  toggle,
  checked,
  disabled,
  onCheckedChange,
}: {
  toggle: SlackToggleDef;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const id = `mp-slack-${toggle.key}`;
  const label = t(toggle.labelKey, toggle.labelDefault);

  return (
    <div
      className={cn(
        "flex items-center gap-4 px-4 transition-colors duration-150",
        "hover:bg-muted/50 focus-within:bg-muted/50",
        !checked && "bg-muted/30",
      )}
    >
      <label
        htmlFor={id}
        className={cn(
          "flex min-h-11 min-w-0 flex-1 flex-col justify-center py-3",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        <span className="text-xs font-medium text-foreground">{label}</span>
        <span className="mt-0.5 max-w-[62ch] text-xs leading-relaxed text-muted-foreground">
          {t(toggle.descriptionKey, toggle.descriptionDefault)}
        </span>
      </label>
      <Switch
        id={id}
        className="cursor-pointer"
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={label}
      />
    </div>
  );
}
